import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNowStrict,
  differenceInDays,
  differenceInMinutes,
  differenceInHours,
  differenceInMonths,
  differenceInYears,
} from 'date-fns';
import { de, enUS, es, fr, it, pt, ptBR } from 'date-fns/locale';

const locales = {
  de,
  en: enUS,
  es,
  fr,
  it,
  pt,
  pt_BR: ptBR,
};

const getLocale = () => {
  if (window.i18n) {
    const locale = window.i18n.global.locale;
    const localeString = typeof locale === 'object' ? locale.value : locale;
    const normalizedLocale = localeString.replace('-', '_');
    return locales[normalizedLocale] || locales[localeString] || enUS;
  }
  return enUS;
};

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat, { locale: getLocale() });
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
const isTwentyFourHourTime = () => {
  const locale = window.i18n?.global?.locale?.value || 'en';
  return ['pt_BR', 'pt', 'es', 'fr', 'de', 'it'].includes(
    locale.replace('-', '_')
  );
};

export const messageTimestamp = (time, dateFormat = 'MMM d, yyyy') => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const locale = getLocale();
  const messageDate = format(messageTime, dateFormat, { locale });
  if (!isSameYear(messageTime, now)) {
    const yearFormat = isTwentyFourHourTime()
      ? 'd LLL y, HH:mm'
      : 'LLL d y, h:mm a';
    return format(messageTime, yearFormat, { locale });
  }
  return messageDate;
};

export const getMessageMetaFormat = () => {
  return isTwentyFourHourTime() ? 'd LLL, HH:mm' : 'LLL d, h:mm a';
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  const unixTime = fromUnixTime(time);
  return formatDistanceToNowStrict(unixTime, {
    addSuffix: true,
    locale: getLocale(),
    roundingMethod: 'floor',
  });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df, { locale: getLocale() });
};

/**
 * Converts a Unix timestamp into a shorter format using localized abbreviations.
 * @param {number} time - Unix timestamp.
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  // SacPlan/Chatplan localized version: Supports both number (timestamp) and string (result of dynamicTime)
  // for backward compatibility during transition.
  if (typeof time === 'string') {
    // Legacy behavior for strings (e.g. from components not yet updated)
    let suffix = '';
    if (withAgo) {
      suffix = window.i18n
        ? ` ${window.i18n.global.t('TIME_UNITS.AGO')}`
        : ' ago';
    }
    const convertToShortTime = time
      .replace(/about|over|almost|/g, '')
      .replace('less than a minute ago', 'now')
      .replace('0 seconds ago', 'now')
      .replace(/ \d+ seconds? ago/, 'now')
      .replace(/an? minute ago/, `1m${suffix}`)
      .replace(/an? hour ago/, `1h${suffix}`)
      .replace(/an? day ago/, `1d${suffix}`)
      .replace(/an? month ago/, `1mo${suffix}`)
      .replace(/an? year ago/, `1y${suffix}`)
      .replace(' minute ago', `m${suffix}`)
      .replace(' minutes ago', `m${suffix}`)
      .replace(' hour ago', `h${suffix}`)
      .replace(' hours ago', `h${suffix}`)
      .replace(' day ago', `d${suffix}`)
      .replace(' days ago', `d${suffix}`)
      .replace(' month ago', `mo${suffix}`)
      .replace(' months ago', `mo${suffix}`)
      .replace(' year ago', `y${suffix}`)
      .replace(' years ago', `y${suffix}`);
    return convertToShortTime;
  }

  const date = fromUnixTime(time);
  const now = new Date();
  const t = window.i18n ? window.i18n.global.t : key => key;

  const diffInMinutes = differenceInMinutes(now, date);
  const diffInHours = differenceInHours(now, date);
  const diffInDays = differenceInDays(now, date);
  const diffInMonths = differenceInMonths(now, date);
  const diffInYears = differenceInYears(now, date);

  const suffix = withAgo ? ` ${t('TIME_UNITS.AGO')}` : '';

  if (diffInMinutes < 1) {
    return 'now';
  }
  if (diffInHours < 1) {
    return `${diffInMinutes}${t('TIME_ABBR.MINUTE', diffInMinutes)}${suffix}`;
  }
  if (diffInDays < 1) {
    return `${diffInHours}${t('TIME_ABBR.HOUR', diffInHours)}${suffix}`;
  }
  if (diffInMonths < 1) {
    return `${diffInDays}${t('TIME_ABBR.DAY', diffInDays)}${suffix}`;
  }
  if (diffInMonths < 12) {
    return `${diffInMonths}${t('TIME_ABBR.MONTH', diffInMonths)}${suffix}`;
  }
  return `${diffInYears}${t('TIME_ABBR.YEAR', diffInYears)}${suffix}`;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
